import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatDate, formatMoney, formatNumber } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { MetricsPanel } from './MetricsPanel';
import { InvoicesPanel } from './InvoicesPanel';
import { ProductPanel } from './ProductPanel';
import { AnnouncementsPanel } from './AnnouncementsPanel';
import { AiRatesPanel } from './AiRatesPanel';
import { FeedbackPanel } from './FeedbackPanel';
import { DataTable, type Column } from '../ui/DataTable';
import { listPlans, listTenants, type PlanRow, type TenantRow } from './api';

/**
 * Safe Cortex's own view of the platform.
 *
 * Read-only. Suspending a tenant, moving it between plans and opening a support session are all
 * actions with consequences a customer feels, and each needs its own confirmation and its own reason
 * field — a row of buttons on a list is how one gets clicked by accident. The API for all of them
 * exists; the flows land next.
 */

const TENANT_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  READ_ONLY: 'warning',
  SUSPENDED: 'danger',
  DELETED: 'neutral',
};

const SUBSCRIPTION_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  SUSPENDED: 'danger',
  CANCELED: 'neutral',
};

type Tab =
  'numbers' | 'product' | 'feedback' | 'ai' | 'tenants' | 'plans' | 'invoices' | 'announcements';

export function AdminPage(): React.JSX.Element {
  // The numbers first: somebody opening the control plane wants to know how it is going.
  const [tab, setTab] = useState<Tab>('numbers');

  return (
    <div className="view view--wide">
      <PageHeader title={t('nav.admin')} description={t('admin.description')} />

      <div className="chips" role="tablist">
        {(
          [
            'numbers',
            'product',
            'feedback',
            'ai',
            'tenants',
            'plans',
            'invoices',
            'announcements',
          ] as Tab[]
        ).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`chip${tab === key ? ' chip--active' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(`admin.tab.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'numbers' && <MetricsPanel />}
      {tab === 'product' && <ProductPanel />}
      {tab === 'feedback' && <FeedbackPanel />}
      {tab === 'ai' && <AiRatesPanel />}
      {tab === 'announcements' && <AnnouncementsPanel />}
      {tab === 'tenants' && <Tenants />}
      {tab === 'plans' && <Plans />}
      {tab === 'invoices' && <InvoicesPanel />}
    </div>
  );
}

function Tenants(): React.JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [status, setStatus] = useState('');

  const tenants = useQuery({
    queryKey: ['admin-tenants', applied, status],
    queryFn: () =>
      listTenants({ ...(applied ? { search: applied } : {}), ...(status ? { status } : {}) }),
  });

  const columns: Column<TenantRow>[] = [
    {
      key: 'name',
      header: t('admin.tenant'),
      render: (tenant) => (
        <span className="cell-stack">
          <span className="strong">{tenant.name}</span>
          <span className="muted small">
            <code>{tenant.slug}</code>
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (tenant) => (
        <Badge tone={TENANT_TONE[tenant.status] ?? 'neutral'}>
          {t(`admin.state.${tenant.status}`)}
        </Badge>
      ),
    },
    {
      key: 'plan',
      header: t('home.plan'),
      render: (tenant) => (
        <span className="cell-stack">
          <span>{tenant.plan_key ?? '—'}</span>
          {tenant.subscription_status && (
            <Badge tone={SUBSCRIPTION_TONE[tenant.subscription_status] ?? 'neutral'}>
              {t(`billing.status.${tenant.subscription_status}`)}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'trial',
      header: t('admin.trialEnds'),
      secondary: true,
      render: (tenant) => formatDate(tenant.trial_ends_at),
    },
    {
      key: 'users',
      header: t('admin.users'),
      align: 'end',
      render: (tenant) => formatNumber(tenant.user_count),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (tenant) => formatDate(tenant.created_at),
    },
  ];

  return (
    <>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(search.trim());
        }}
      >
        <select
          className="select"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('admin.filterStatus')}
        >
          <option value="">{t('admin.allStatuses')}</option>
          {['ACTIVE', 'READ_ONLY', 'SUSPENDED', 'DELETED'].map((entry) => (
            <option key={entry} value={entry}>
              {t(`admin.state.${entry}`)}
            </option>
          ))}
        </select>

        <label className="field-search">
          <input
            type="search"
            value={search}
            placeholder={t('admin.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <button type="submit" className="button button--quiet button--compact">
          {t('crm.search')}
        </button>
      </form>

      <Panel>
        <DataTable
          tableKey="admin"
          rows={tenants.data?.tenants}
          columns={columns}
          rowKey={(tenant) => tenant.id}
          isLoading={tenants.isLoading}
          error={tenants.error}
          onRetry={() => void tenants.refetch()}
          onRowClick={(tenant) => navigate(`/admin/tenants/${tenant.id}`)}
          empty={{ title: t('admin.noTenants') }}
        />
      </Panel>
    </>
  );
}

function Plans(): React.JSX.Element {
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: listPlans });

  const columns: Column<PlanRow>[] = [
    {
      key: 'name',
      header: t('admin.plan'),
      render: (plan) => (
        <span className="cell-stack">
          <span className="strong">{plan.name}</span>
          <span className="muted small">{plan.description ?? plan.key}</span>
        </span>
      ),
    },
    {
      key: 'price',
      header: t('admin.price'),
      render: (plan) => (
        <span className="cell-stack">
          <span>{formatMoney(plan.price_cents, plan.currency)}</span>
          <span className="muted small">{t(`admin.period.${plan.billing_period}`)}</span>
        </span>
      ),
    },
    {
      key: 'trial',
      header: t('admin.trialDays'),
      secondary: true,
      align: 'end',
      render: (plan) => String(plan.trial_days),
    },
    {
      key: 'features',
      header: t('billing.features'),
      secondary: true,
      align: 'end',
      render: (plan) => String(plan.features.length),
    },
    {
      key: 'flags',
      header: '',
      align: 'end',
      render: (plan) => (
        <span className="cell-inline">
          {plan.is_default && <Badge tone="info">{t('admin.default')}</Badge>}
          {!plan.active && <Badge tone="neutral">{t('admin.inactive')}</Badge>}
          {!plan.is_public && <Badge tone="warning">{t('admin.private')}</Badge>}
        </span>
      ),
    },
  ];

  return (
    <Panel>
      <DataTable
        tableKey="admin-2"
        rows={plans.data?.plans}
        columns={columns}
        rowKey={(plan) => plan.key}
        isLoading={plans.isLoading}
        error={plans.error}
        onRetry={() => void plans.refetch()}
        empty={{ title: t('admin.noPlans') }}
      />
    </Panel>
  );
}
